import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, Minus, Plug } from "lucide-react";
import VeyraLogo from "@/components/brand/VeyraLogo";
import { Notice } from "@/components/common/States";
import { useStudio } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import GuestOnboarding from "@/components/onboarding/GuestOnboarding";
import FormatPicker from "@/components/generator/FormatPicker";
import { STANDARD_FORMATS, IMAGE_RESOLUTIONS } from "@/domain/formats";
import { notifySaved } from "@/lib/notify";
import { t, LANGUAGES, setLanguage } from "@/i18n";

const STEP_KEYS = ["access", "workspace", "connections", "assistant", "budget", "first"];

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-[#a3a3a3]">{hint}</span>}
  </label>
);

const StepStatusIcon = ({ status, isCurrent, index }) => {
  if (status === "completed") return <Check size={12} />;
  if (status === "skipped") return <Minus size={12} />;
  return isCurrent ? <span className="text-[#f5b544]">{index + 1}</span> : index + 1;
};

const OwnerOnboarding = () => {
  const { state, dispatch } = useStudio();
  const ob = state.onboarding;
  const navigate = useNavigate();
  const [step, setStep] = useState(Math.min(ob.step || 0, 5));
  const [projectName, setProjectName] = useState("");
  const [connectionName, setConnectionName] = useState("Apimart");
  const update = (patch) => dispatch({ type: "ONBOARDING_UPDATE", payload: patch });
  const go = (n) => { setStep(n); update({ step: n }); };
  const steps = t.onboarding.steps;
  const key = STEP_KEYS[step];
  const markStatus = (status) => update({ stepStatus: { ...ob.stepStatus, [key]: status } });

  const complete = (then) => { dispatch({ type: "ONBOARDING_COMPLETE" }); then(); };
  const createProject = () => {
    const id = `prj_${Date.now().toString(36)}`;
    dispatch({ type: "PROJECT_CREATE", payload: { id, name: projectName.trim() || t.common.untitled, format: ob.workspacePrefs.format || "16:9" } });
    markStatus("completed");
    complete(() => navigate(`/projects/${id}/storyboard`));
  };
  const addConnection = () => {
    dispatch({ type: "CONNECTION_CREATE", payload: { name: connectionName.trim() || "Apimart", provider: "apimart", capabilities: ["image", "video"] } });
    markStatus("completed");
    notifySaved();
  };

  const body = [
    <div key="0" className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-[#a3a3a3]">{t.onboarding.access.body}</p>
      <Field label={t.onboarding.access.owner} hint={t.onboarding.access.note}>
        <input data-testid="onboarding-owner-name" className="v-input" placeholder={t.onboarding.access.ownerPh} value={ob.ownerName} onChange={(e) => update({ ownerName: e.target.value })} onBlur={notifySaved} />
      </Field>
      <Field label={t.onboarding.access.language} hint={t.onboarding.access.languageHint}>
        <div className="flex gap-2">
          {LANGUAGES.map((l) => (
            <button key={l.code} data-testid={`onboarding-language-${l.code}`} onClick={() => { update({ language: l.code }); setLanguage(l.code); notifySaved(); }} className={`v-btn-ghost v-btn-sm ${ob.language === l.code ? "border-[#f5b544] text-[#f5b544]" : ""}`}>
              {l.code === "es" ? t.onboarding.workspace.es : t.onboarding.workspace.en}
            </button>
          ))}
        </div>
      </Field>
      <Notice tone="warn" testId="onboarding-access-notice">{t.onboarding.accessComingSoon}</Notice>
    </div>,
    <div key="1" className="flex flex-col gap-4">
      <Field label={t.onboarding.workspace.name} hint={t.onboarding.workspace.brandNote}>
        <input data-testid="onboarding-workspace-name" className="v-input" placeholder={t.onboarding.workspace.namePh} value={ob.workspaceName} onChange={(e) => update({ workspaceName: e.target.value })} onBlur={notifySaved} />
      </Field>
      <Field label={t.onboarding.workspace.formatPref} hint={t.onboarding.workspace.formatPrefHint}>
        <FormatPicker formats={STANDARD_FORMATS} value={ob.workspacePrefs.format} onChange={(f) => { update({ workspacePrefs: { ...ob.workspacePrefs, format: f } }); notifySaved(); }} testPrefix="onboarding-format" />
      </Field>
      <Field label={t.onboarding.workspace.resolutionPref} hint={t.onboarding.workspace.resolutionPrefHint}>
        <div className="flex flex-wrap gap-2">
          {IMAGE_RESOLUTIONS.map((r) => (
            <button key={r} data-testid={`onboarding-resolution-${r}`} onClick={() => { update({ workspacePrefs: { ...ob.workspacePrefs, imageResolution: r } }); notifySaved(); }} className={`v-btn-ghost v-btn-sm ${ob.workspacePrefs.imageResolution === r ? "border-[#f5b544] text-[#f5b544]" : ""}`}>{r}</button>
          ))}
        </div>
      </Field>
    </div>,
    <div key="2" className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-[#a3a3a3]">{t.onboarding.connections.body}</p>
      {state.connections.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid="onboarding-connections-list">
          {state.connections.map((c) => (
            <li key={c.id} data-testid={`onboarding-connection-${c.id}`} className="flex items-center gap-2 rounded-lg border border-[#303030] bg-[#151515] px-3 py-2 text-sm">
              <Plug size={14} className="text-[#6b6b6b]" />
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-[#a3a3a3]">· {c.provider}</span>
              <span className="ml-auto text-xs text-[#a3a3a3]">{t.connections.status.not_configured}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <Field label={t.settings.connectionName}>
          <input data-testid="onboarding-connection-name" className="v-input" placeholder={t.settings.connectionNamePh} value={connectionName} onChange={(e) => setConnectionName(e.target.value)} />
        </Field>
        <button data-testid="onboarding-add-connection" className="v-btn-primary v-btn-sm shrink-0" onClick={addConnection}>{t.onboarding.connections.add}</button>
      </div>
      <Notice testId="onboarding-connections-notice">{t.onboarding.connections.explore}</Notice>
    </div>,
    <div key="3" className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-[#a3a3a3]">{t.onboarding.assistant.body}</p>
      <Field label={t.onboarding.assistant.provider} hint={t.onboarding.assistant.disabled}>
        <input data-testid="onboarding-text-provider" className="v-input" disabled placeholder={t.onboarding.assistant.providerPh} />
      </Field>
    </div>,
    <div key="4" className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-[#a3a3a3]">{t.onboarding.budget.body}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t.onboarding.budget.limit}>
          <input data-testid="onboarding-budget-limit" type="number" min="0" className="v-input" placeholder={t.onboarding.budget.noLimit} value={ob.budget.monthlyLimit ?? ""} onChange={(e) => update({ budget: { ...ob.budget, monthlyLimit: e.target.value === "" ? null : Number(e.target.value) } })} onBlur={notifySaved} />
        </Field>
        <Field label={t.onboarding.budget.alert}>
          <input data-testid="onboarding-budget-alert" type="number" min="1" max="100" className="v-input" value={ob.budget.alertPct} onChange={(e) => update({ budget: { ...ob.budget, alertPct: Number(e.target.value) } })} onBlur={notifySaved} />
        </Field>
      </div>
      <label className="flex items-start gap-2.5 rounded-lg border border-[#303030] bg-[#151515] p-3">
        <input data-testid="onboarding-budget-block" type="checkbox" className="mt-0.5 accent-[#f5b544]" checked={ob.budget.blockOnLimit} onChange={(e) => { update({ budget: { ...ob.budget, blockOnLimit: e.target.checked } }); notifySaved(); }} />
        <span>
          <span className="block text-sm font-medium">{t.onboarding.budget.block}</span>
          <span className="mt-0.5 block text-xs text-[#a3a3a3]">{t.onboarding.budget.blockHint}</span>
        </span>
      </label>
    </div>,
    <div key="5" className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-[#a3a3a3]">{t.onboarding.first.body}</p>
      <Field label={t.onboarding.first.projectName}>
        <input data-testid="onboarding-project-name" className="v-input" placeholder={t.onboarding.first.projectPh} value={projectName} onChange={(e) => setProjectName(e.target.value)} />
      </Field>
    </div>,
  ];

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-[#101010] text-[#f4f1ec]">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 md:grid-cols-[300px_1fr]">
        <aside className="flex flex-col border-b border-[#303030] p-8 md:border-b-0 md:border-r">
          <VeyraLogo size={30} />
          <div className="mt-10">
            <div className="v-eyebrow mb-1">{t.onboarding.title}</div>
            <p className="text-sm text-[#a3a3a3]">{t.onboarding.subtitle}</p>
          </div>
          <ol className="mt-8 hidden flex-col gap-1 md:flex" data-testid="onboarding-steps">
            {steps.map((s, i) => {
              const st = ob.stepStatus[STEP_KEYS[i]];
              return (
                <li key={s}>
                  <button data-testid={`onboarding-step-${i}`} onClick={() => go(i)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${i === step ? "bg-[#1b1b1b] text-[#f4f1ec]" : "text-[#a3a3a3] hover:text-[#f4f1ec]"}`}>
                    <span data-testid={`onboarding-step-status-${i}`} title={t.stepStatus[st]} className={`flex h-6 w-6 items-center justify-center rounded-full border font-mono text-[11px] ${st === "completed" ? "border-[#f5b544] bg-[#f5b544] text-[#1a1206]" : st === "skipped" ? "border-[#6b6b6b] text-[#6b6b6b]" : i === step ? "border-[#f5b544] text-[#f5b544]" : "border-[#303030]"}`}>
                      <StepStatusIcon status={st} isCurrent={i === step} index={i} />
                    </span>
                    {s}
                  </button>
                </li>
              );
            })}
          </ol>
          <button data-testid="onboarding-explore" onClick={() => { update({ step }); navigate("/"); }} className="mt-auto pt-8 text-left text-xs text-[#a3a3a3] hover:text-[#f4f1ec]">{t.onboarding.exploreWithout}</button>
        </aside>

        <section className="flex flex-col p-8 md:p-14">
          <div className="v-eyebrow mb-2 md:hidden">{`${step + 1} / ${steps.length}`}</div>
          <h1 data-testid="onboarding-step-title" className="text-3xl font-semibold tracking-tight">{[t.onboarding.access.title, t.onboarding.workspace.title, t.onboarding.connections.title, t.onboarding.assistant.title, t.onboarding.budget.title, t.onboarding.first.title][step]}</h1>
          <div className="mt-8 max-w-xl v-fade-up" key={step}>{body[step]}</div>
          <div className="mt-10 flex max-w-xl items-center justify-between gap-2">
            <button data-testid="onboarding-back" className="v-btn-ghost" disabled={step === 0} onClick={() => go(step - 1)}><ChevronLeft size={16} />{t.common.back}</button>
            <div className="flex gap-2">
              {(step === 2 || step === 3) && <button data-testid="onboarding-skip" className="v-btn-ghost" onClick={() => { markStatus("skipped"); go(step + 1); }}>{t.common.skip}</button>}
              {step === 2 && <button data-testid="onboarding-next" className="v-btn-primary" disabled={state.connections.length === 0} onClick={() => { markStatus("completed"); go(step + 1); }}>{t.common.next}</button>}
              {(step === 0 || step === 1 || step === 4) && <button data-testid="onboarding-next" className="v-btn-primary" onClick={() => { markStatus("completed"); go(step + 1); }}>{t.common.next}</button>}
              {step === 5 && (
                <>
                  <button data-testid="onboarding-go-home" className="v-btn-ghost" onClick={() => { markStatus("skipped"); complete(() => navigate("/")); }}>{t.onboarding.first.goHome}</button>
                  <button data-testid="onboarding-create-project" className="v-btn-primary" onClick={createProject}>{t.onboarding.first.createAndOpen}</button>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const OnboardingPage = () => {
  const { isOwner } = useAuth();
  if (!isOwner) {
    return <GuestOnboarding />;
  }
  return <OwnerOnboarding />;
};

export default OnboardingPage;
